import React, { memo, useEffect, useState, useRef, useMemo } from "react";
import { LiveMap } from "@liveblocks/core";
import { ClientSideSuspense, LiveblocksProvider, RoomProvider, useSelf } from "@liveblocks/react/suspense";
import { useStorageStore } from "Hooks/useStorageStores";
import { useHistory, useLocation } from "react-router";
import { generatePath, useParams } from "react-router-dom";
import CircularProgress from "@material-ui/core/CircularProgress";
import CONFIG from "config/config";
import { useMultiplayerState } from "pages/flyer/builder/drawer/hooks";
import { useMultiplayerAssets } from "pages/flyer/builder/drawer/hooks/useMultiplayerAssets";
import { StyledLoader } from "pages/flyer/builder/drawer/styled.ts";
import { useGetFlyerInfo } from "pages/flyer/flyerTypePage/useGetFlyerInfo";
import { Helmet } from "react-helmet";
import ROUTES from "config/routing";
import { FORMATS } from "./constants";
import { getEffectiveFormat } from "./formats/formatsStore";
import { useCampaignFormats } from "./formats/useFormats";
import "./disable-swipe-back.css";
import { Tldraw } from "./drawer/index";
import { TldrawApp } from "./drawer/state";
import "./index.css";
import { useStore } from "../../../store";
import { buildLiveblocksAuthEndpoint, buildLiveblocksUserPayload } from "./utils/liveblocksAuth";
import { VariablesProvider } from "../../../components/VariablesPanel/context/VariablesContext";
import BrowserWarningModal from "../../../components/BrowserWarning/BrowserWarningModal";
import MobileBlockerModal from "../../../components/MobileBlocker/MobileBlockerModal";
import { getRoomId } from "./utils";
import { getRoomStorage } from "./PublicBuilder";
import useTLDrawStorage from "./hooks/useTLDrawStorage";
import { LoadingUI } from "./drawer/components/Loading";
import { ImageUploadProvider } from "./drawer/contexts/ImageUploadContext";
import { PDFUploadProvider } from "./drawer/contexts/PDFUploadContext";
import { FontsProvider } from "./drawer/contexts/FontsContext";
import { useBrandKitStore } from "../../../store/brandKit";
import { usePlaceholderPersistence } from "../../../components/VariablesPanel/hooks/usePlaceholderPersistence";
import { apiClient } from "../../../module/api";

const EditorInner = memo(({ roomId, designId, countryCode, flyerType }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { error, ...events } = useMultiplayerState(roomId, countryCode, flyerType);

  // Persist placeholder config to Liveblocks storage
  usePlaceholderPersistence();
  const { onAssetCreate, onAssetDelete } = useMultiplayerAssets(roomId);

  // Liveblocks 2.x exposes the authenticated identity as `me.id` / `me.info`
  // (the `userInfo` we send from the `/liveblocks/auth` endpoint). The legacy
  // `me.presence?.info` path is a mutable cursor-payload slot and is always
  // undefined here — reading it gave every collaborator the "DefaultName"
  // fallback on remote cursors. See `oppizi-api/src/routes/liveblocks.js` →
  // `buildUserInfo` for the server side.
  const id = useSelf((me) => me.id) || "defaultId";
  const infoName = useSelf((me) => me.info?.name);
  const infoColor = useSelf((me) => me.info?.color);
  const name = infoName || "Guest";
  const color = infoColor || "#4FC3F7";

  // Stabilize user object to prevent unnecessary re-renders
  // useSelf returns a new object on each render, so we cache it
  const userRef = useRef({ id: "defaultId", color: "#4FC3F7", name: "Guest" });
  if (userRef.current.id !== id || userRef.current.color !== color || userRef.current.name !== name) {
    userRef.current = { id, color, name };
  }

  const store = useStorageStore({ user: userRef.current });

  return (
    <div className="tldraw">
      <Tldraw
        tlStore={store}
        onAssetCreate={onAssetCreate}
        onAssetDelete={onAssetDelete}
        {...events}
        designId={designId}
      />
    </div>
  );
});

export const Editor = memo(({ roomId, designId, countryCode, flyerType }) => {
  return (
    <VariablesProvider>
      <PDFUploadProvider>
        <ImageUploadProvider>
          <FontsProvider>
            <EditorInner roomId={roomId} designId={designId} countryCode={countryCode} flyerType={flyerType} />
          </FontsProvider>
        </ImageUploadProvider>
      </PDFUploadProvider>
    </VariablesProvider>
  );
});

function FlyerBuilder() {
  const {
    campaign: { flyerType: campaignFlyerType, isSubmitted, id: campaignId },
    client: { id: clientId },
    country: { code },
    user,
    user: { id: userId, firstName },
  } = useStore();

  // PAD-32920: a campaign with no format yet (an ADM draft whose design was uploaded before
  // a format was picked) used to hold this page on the loading spinner forever, even though
  // the route names the format to edit in. The editor already treats the route format as
  // authoritative (Tldraw's effectiveFormat, BottomToolPanel writes it back to the campaign),
  // so fall back to it here too.
  const { format: routeFormat } = useParams();
  const { isPending: formatsPending } = useCampaignFormats({ refreshOnMount: true });
  const flyerType = campaignFlyerType || (getEffectiveFormat(routeFormat) ? routeFormat : "");
  // A catalogue-only code has no built-in config, so hold the spinner until the API answers (or the flag resolves off)
  const awaitingCatalogue = !!flyerType && !FORMATS[flyerType] && !getEffectiveFormat(flyerType) && formatsPending;

  const { storage, loading } = useTLDrawStorage();
  const history = useHistory();
  const location = useLocation();
  const flyerInfo = useGetFlyerInfo();

  // Stabilize storage to prevent RoomProvider from re-mounting
  const initialStorageRef = useRef(storage);

  const defaultDocument = TldrawApp.defaultDocument;

  // Obtain designId and newDesign flag from query params
  const designIdFromParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("designId");
  }, [location.search]);

  const isNewDesign = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("newDesign") === "true";
  }, [location.search]);

  // Prioritize designId from query params over flyerInfo.activeRoomId
  const activeDesignId = designIdFromParams || flyerInfo.flyerInfo?.activeRoomId;

  // Stabilize the designId to prevent RoomProvider re-mounting when flyerInfo updates with stale data
  const initialDesignIdRef = useRef(null);

  useEffect(() => {
    if (designIdFromParams && designIdFromParams !== initialDesignIdRef.current) {
      // New designId in URL → reset to use the new one
      initialDesignIdRef.current = designIdFromParams;
    } else if (activeDesignId && !initialDesignIdRef.current) {
      // First time → establish the initial designId
      initialDesignIdRef.current = activeDesignId;
    }
  }, [designIdFromParams, activeDesignId]);

  // Use the stabilized designId to prevent re-mounts
  const stableDesignId = initialDesignIdRef.current || activeDesignId;

  // Fix for PAD-25702: Prevent navigation when window is resized
  const originalNavigate = useRef(history.push);

  useEffect(() => {
    let isWindowBeingResized = false;
    let resizeTimer;

    const handleWindowResize = () => {
      isWindowBeingResized = true;
      clearTimeout(resizeTimer);

      // Reset the flag after resize operations complete
      resizeTimer = setTimeout(() => {
        isWindowBeingResized = false;
      }, 250);
    };

    // Override history.push temporarily during resize to prevent unwanted navigation
    const safePush = (path, state) => {
      // Only allow navigation if we're not in the middle of a resize operation
      // and if it's not an unwanted redirect to template selection
      if (!isWindowBeingResized || (!path.includes("template") && !path.includes("creative"))) {
        originalNavigate.current(path, state);
      }
    };

    // Store original and override
    originalNavigate.current = history.push;
    history.push = safePush;

    window.addEventListener("resize", handleWindowResize);

    return () => {
      // Restore original navigation
      history.push = originalNavigate.current;
      window.removeEventListener("resize", handleWindowResize);
      clearTimeout(resizeTimer);
    };
  }, [history]);

  // Load Brand Kit data on design init so it's available for font tags, text, placeholders, etc.
  // Must complete before canvas renders so the default font is available.
  const loadBrandKit = useBrandKitStore((s) => s.loadBrandKit);
  const brandKitLoading = useBrandKitStore((s) => s.isLoading);
  const [brandKitLoaded, setBrandKitLoaded] = useState(false);
  useEffect(() => {
    loadBrandKit().finally(() => setBrandKitLoaded(true));
  }, [loadBrandKit]);

  const authEndpointUrl = process.env.REACT_APP_LIVEBLOCKS_AUTH_URL;
  const userPayload = useMemo(
    () => buildLiveblocksUserPayload(user),
    [user?.id, user?.firstName, user?.lastName, user?.email, user?.clientName, user?.profilePicture]
  );
  const authEndpointCallback = useMemo(
    () => buildLiveblocksAuthEndpoint(authEndpointUrl, userPayload),
    [authEndpointUrl, userPayload]
  );
  // Liveblocks `resolveUsers` callback — kept as a safety net for any
  // Liveblocks internals that consume it (notifications, inbox, etc.).
  // Our own UI doesn't rely on this: `useLiveblocksUser` reads from our
  // `/platform/users/resolve` endpoint directly (see `mentionCache.ts`), which
  // bypasses Liveblocks' server-side userInfo cache.
  const resolveUsers = useMemo(() => {
    const currentUserId = user?.id ? `user-${user.id}` : null;
    const currentUserName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email || "You";
    const currentUserAvatar = user?.profilePicture || "";

    return async ({ userIds }) => {
      const toLookup = userIds.filter((id) => id !== currentUserId);
      const looked = {};
      if (toLookup.length > 0) {
        const rows = await apiClient.resolveUsersBatch({ ids: toLookup });
        rows.forEach((u) => {
          if (u?.id) looked[u.id] = u;
        });
      }
      return userIds.map((id) => {
        if (id === currentUserId) {
          return { name: currentUserName, color: "#4FC3F7", avatar: currentUserAvatar };
        }
        const match = looked[id];
        if (match) {
          return {
            name: match.name || "User",
            color: "#4FC3F7",
            avatar: match.avatar || "",
          };
        }
        return undefined;
      });
    };
  }, [user?.id, user?.firstName, user?.lastName, user?.email, user?.profilePicture]);

  if (isSubmitted) {
    history.push(generatePath(ROUTES.CAMPAIGN_BUILDER, { campaignId, clientId }));
  }

  // Use activeDesignId instead of flyerInfo.flyerInfo?.activeRoomId
  // Allow rendering without flyerType when creating a new design (empty state will show)
  if (
    flyerInfo.isLoading ||
    !stableDesignId ||
    (!flyerType && !isNewDesign) ||
    loading ||
    !brandKitLoaded ||
    awaitingCatalogue
  ) {
    return (
      <>
        {/* Mount the blocker INSIDE the loading-gate return too, so a
            mobile user lands on the blocker immediately instead of
            staring at the spinner while data prefetches. */}
        <MobileBlockerModal />
        <StyledLoader>
          <CircularProgress color="secondary" />
        </StyledLoader>
      </>
    );
  }

  if (flyerType) {
    defaultDocument.format = flyerType;
  }
  const roomId = getRoomId(stableDesignId);
  const designId = stableDesignId;

  return (
    <>
      <Helmet>
        <title>Flyer Builder</title>
      </Helmet>
      {/* Hard mobile blocker. Renders ABOVE BrowserWarningModal so a
          user on Safari Mobile sees the more actionable problem
          first (small screen, not browser choice). Reactive to
          resize: rotating an iPad past 900px width hides it. */}
      <MobileBlockerModal />
      {/* Browser-compatibility nag. Self-contained: checks the UA on
          mount, only renders on non-Chrome/Firefox, and remembers its
          dismissal in module scope so users see it on every reload.
          Lives here (outside the Liveblocks providers) so it shows
          even before the room finishes loading. */}
      <BrowserWarningModal />
      <LiveblocksProvider authEndpoint={authEndpointCallback} resolveUsers={resolveUsers}>
        <RoomProvider id={roomId} initialPresence={{ presence: undefined }} initialStorage={initialStorageRef.current}>
          <ClientSideSuspense fallback={<LoadingUI />}>
            <Editor roomId={roomId} designId={designId} countryCode={code} flyerType={flyerType || ""} />
          </ClientSideSuspense>
        </RoomProvider>
      </LiveblocksProvider>
    </>
  );
}

export default FlyerBuilder;
